import { authenticatedRequest } from './authApi';
import { getAuthToken } from '../utils/auth';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { localizedValue } from '../localization/presentationLanguage';
const clashError = (es, en) => localizedValue({ es, en });

const API_BASE = REST_API_ORIGIN;

async function handleJson(res) {
  const contentType = res.headers.get('content-type') || '';
  let data = null;

  if (/application\/json/i.test(contentType)) {
    try { data = await res.json(); } catch {}
  } else {
    try {
      const text = await res.text();
      if (text) data = { message: text };
    } catch {}
  }

  if (!res.ok) {
    const err = new Error(data?.message || data?.error || res.statusText || 'Error');
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data ?? { ok: true };
}

async function fetchJson(url, options = {}) {
  let r;
  try {
    r = await authenticatedRequest(url, {
      rawResponse: true,
      credentials: 'include',
      cache: 'no-store',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      diagSource: 'zodiacClashApi',
    });
  } catch (e) {
    const err = new Error(clashError('Sin conexión. Verificá tu internet.', 'No connection. Check your internet connection.'));
    err.cause = e;
    err.status = 0;
    throw err;
  }
  return handleJson(r);
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeTodayPayload(data) {
  const payload = data && typeof data === 'object' ? data : {};
  const votesA = toNumber(payload.votesA, 0);
  const votesB = toNumber(payload.votesB, 0);
  const totalVotes = toNumber(payload.totalVotes, votesA + votesB);

  let pctA = toNumber(payload.pctA, 0);
  let pctB = toNumber(payload.pctB, 0);

  if ((!pctA && !pctB) && totalVotes > 0) {
    pctA = Math.round((votesA / totalVotes) * 100);
    pctB = 100 - pctA;
  }

  return {
    ok: payload.ok !== false,
    dayKey: String(payload.dayKey || ''),
    clashId: String(payload.clashId || payload.dayId || ''),
    signA: String(payload.signA || ''),
    signB: String(payload.signB || ''),
    votesA,
    votesB,
    totalVotes,
    pctA,
    pctB,
    myVotes: toNumber(payload.myVotes, 0),
    picked: payload.picked || payload.choice || null,
    reward: payload.reward || null,
    rewardGranted: !!payload.rewardGranted,
    completed: !!payload.completed,
    results: Array.isArray(payload.results) ? payload.results : [],
    insight: payload.insight || null,
    ...payload,
  };
}

function normalizeDominionPayload(data) {
  const payload = data && typeof data === 'object' ? data : {};
  return {
    ok: payload.ok !== false,
    range: String(payload.range || ''),
    dominion: Array.isArray(payload.dominion) ? payload.dominion : [],
    mySignRank: toNumber(payload.mySignRank, 0),
    totalVotes: toNumber(payload.totalVotes, 0),
    elementDominion: Array.isArray(payload.elementDominion) ? payload.elementDominion : [],
    trend: payload.trend || null,
    ...payload,
  };
}

function normalizeVotePayload(data) {
  const payload = normalizeTodayPayload(data);
  return {
    ...payload,
    votesLeft: toNumber(payload.votesLeft, Math.max(0, 3 - toNumber(payload.myVotes, 0))),
    streak: toNumber(payload.streak, 0),
    bonusXp: toNumber(payload.bonusXp, 0),
    ritualCompleted: !!payload.ritualCompleted,
  };
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const VALID_RANGES = new Set(['today', '7d', '30d', 'all']);
const VALID_PICKS = new Set(['A', 'B']);

/**
 * GET /api/zodiac-clash/today
 */
export async function getZodiacClashToday() {
  const data = await fetchJson(`${API_BASE}/api/zodiac-clash/today`, {
    method: 'GET',
    headers: {
      ...authHeaders(),
    },
  });
  return normalizeTodayPayload(data);
}

/**
 * GET /api/zodiac-clash/dominion?range=today|7d|30d|all
 */
export async function getZodiacClashDominion({ range = '7d' } = {}) {
  const safeRange = VALID_RANGES.has(String(range || '').trim()) ? String(range).trim() : '7d';
  const url = new URL(`${API_BASE}/api/zodiac-clash/dominion`);
  url.searchParams.set('range', safeRange);

  const data = await fetchJson(url.toString(), {
    method: 'GET',
    headers: {
      ...authHeaders(),
    },
  });

  return normalizeDominionPayload(data);
}

/**
 * POST /api/zodiac-clash/vote
 * Forma final: { dayKey, clashId, picked: "A" | "B" }
 * Compatibilidad: acepta { dayId, choice }
 */
export async function voteZodiacClash({ dayKey, clashId, picked, dayId, choice } = {}) {
  const token = getAuthToken();
  if (!token) {
    const err = new Error(clashError('No estás autenticado. Iniciá sesión.', 'You are not authenticated. Please log in.'));
    err.status = 401;
    throw err;
  }

  const finalClashId = clashId || dayId || null;
  const finalPicked = picked || choice || null;

  if (!dayKey || typeof dayKey !== 'string') {
    const err = new Error('dayKey requerido para votar.');
    err.status = 400;
    throw err;
  }
  if (!finalClashId || typeof finalClashId !== 'string') {
    const err = new Error('clashId requerido para votar.');
    err.status = 400;
    throw err;
  }
  if (!VALID_PICKS.has(finalPicked)) {
    const err = new Error(clashError('Voto inválido: debe ser "A" o "B".', 'Invalid vote: it must be "A" or "B".'));
    err.status = 400;
    throw err;
  }

  try {
    const data = await fetchJson(`${API_BASE}/api/zodiac-clash/vote`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        dayKey,
        clashId: finalClashId,
        picked: finalPicked,
      }),
    });

    return normalizeVotePayload(data);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      try {
        console.warn('[ZodiacClash] vote failed', {
          status: e?.status,
          payload: e?.payload,
          sent: { dayKey, clashId: finalClashId, picked: finalPicked },
        });
      } catch {}
    }

    if (e.status === 401) {
      e.message = clashError('Sesión inválida o expirada. Iniciá sesión nuevamente.', 'Your session is invalid or expired. Please log in again.');
    } else if (e.status === 403) {
      const code = String(e.payload?.error || '').toLowerCase();
      if (code.includes('email')) {
        e.message = clashError('Tu email no está verificado. Verificalo para poder votar.', 'Your email is not verified. Verify it before voting.');
      } else if (code.includes('limit') || code.includes('votes')) {
        e.message = clashError('Ya usaste tus votos de hoy. Volvé mañana.', 'You have used today’s votes. Come back tomorrow.');
      } else {
        e.message = clashError('No tenés permisos para votar.', 'You do not have permission to vote.');
      }
    } else if (e.status === 400) {
      const code = String(e.payload?.error || '').toLowerCase();
      if (code.includes('bad_clash_id')) {
        e.message = clashError('El duelo no corresponde al día de hoy. Refrescá la pantalla.', 'This matchup is not for today. Refresh the screen.');
      } else if (code.includes('bad_pick')) {
        e.message = clashError('Voto inválido. Probá nuevamente.', 'Invalid vote. Try again.');
      } else if (code.includes('day')) {
        e.message = clashError('El día del duelo es inválido. Refrescá la pantalla e intentá de nuevo.', 'The matchup date is invalid. Refresh the screen and try again.');
      } else {
        e.message = clashError('Datos de voto inválidos. Refrescá la pantalla e intentá de nuevo.', 'The vote data is invalid. Refresh the screen and try again.');
      }
    } else if (e.status === 409) {
      e.message = 'Ya votaste este duelo.';
    }

    throw e;
  }
}
